import { ThemeProvider } from '@material-ui/core';
import { ErrorBoundary } from '@rollbar/react';
import { Amplify } from 'aws-amplify';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import ReactGA from 'react-ga4';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import packageInfo from '../package.json';
import './App.css';
import eatclubIco from './assets/favicons/eatclub.ico';
import obeeIco from './assets/favicons/obee.ico';
import awsconfig from './aws-exports';
import { LegacyRedirect } from './components/LegacyRedirect';
import { Toast } from './components/Toast';
import { applyPreviewStepAction } from './data/actions/bookingAction';
import {
  fetchObeeIntegrationsAction,
  initializeFacebookAction,
} from './data/actions/integrationsAction';
import { setPreviewWidgetSettingsAction } from './data/actions/restaurantAction';
import { bookingSteps } from './data/models/Booking';
import { integrationsPropTypes } from './data/models/Integrations';
import { restaurantPropTypes } from './data/models/Restaurant';
import SwitchRoutes from './routes/SwitchRoutes';
import { ErrorDisplay } from './screens/ErrorDisplay';
import { createTheme } from './theme';
import { trackGooglePageview } from './utils/analytics';
import { isObee } from './utils/constants';
import { isEmpty, setFavicon } from './utils/helpers';
import { getPreviewPortalOrigin, isPreviewMode } from './utils/previewMode';

Amplify.configure(awsconfig);

const App = ({
  integrations,
  initializeFacebook,
  fetchObeeIntegrations,
  restaurant,
  setPreviewWidgetSettings,
  applyPreviewStep,
}) => {
  // Live preview mode: when embedded by the partner portal with ?preview=true,
  // accept draft widget settings via postMessage and apply them without saving
  const previewPortalOrigin = getPreviewPortalOrigin();
  const isPreview = isPreviewMode();

  // The portal can navigate before the restaurant has loaded, so remember the
  // step and re-apply it once real areas are available for the prefill
  const previewStepRef = useRef(null);

  useEffect(() => {
    if (!isPreview) {
      return undefined;
    }

    const navigableSteps = [
      bookingSteps.availability,
      bookingSteps.details,
      bookingSteps.review,
      bookingSteps.confirm,
    ];

    const handleMessage = (event) => {
      if (event.source !== window.parent || event.origin !== previewPortalOrigin) {
        return;
      }

      // Portal pager: jump to a widget step with the booking state prefilled
      if (event.data?.type === 'ec:widgetPreviewNavigate') {
        if (navigableSteps.includes(event.data?.step)) {
          previewStepRef.current = event.data.step;
          applyPreviewStep(event.data.step);
        }
        return;
      }

      if (event.data?.type !== 'ec:widgetPreviewSettings') {
        return;
      }

      const settings = event.data?.settings;
      if (typeof settings !== 'object' || settings === null) {
        return;
      }

      // Only allow scalar values so the merged theme can't be polluted with objects
      const filteredSettings = Object.fromEntries(
        Object.entries(settings).filter(
          ([, value]) => value === null || ['string', 'number', 'boolean'].includes(typeof value),
        ),
      );

      setPreviewWidgetSettings(filteredSettings);
    };

    window.addEventListener('message', handleMessage);

    // Let the portal know the widget is ready to receive the first snapshot
    window.parent.postMessage({ type: 'ec:widgetPreviewReady' }, previewPortalOrigin);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Re-apply the last navigated step once the restaurant loads, so the prefill
  // is rebuilt from the venue's real areas instead of fallbacks
  useEffect(() => {
    if (isPreview && restaurant.success && previewStepRef.current !== null) {
      applyPreviewStep(previewStepRef.current);
    }
  }, [restaurant.success]);

  useEffect(() => {
    if (restaurant.data.backgroundType === 'image') {
      window.document.body.style.backgroundImage = `url(${restaurant.data.backgroundImageUrl})`;
      window.document.body.style.backgroundSize = `cover`;
      window.document.body.style.backgroundRepeat = 'no-repeat';
      window.document.body.style.backgroundPosition = 'center';
      window.document.body.style.backgroundAttachment = 'fixed';
    } else {
      window.document.body.style.background = restaurant.data.backgroundValue;
    }
  }, [
    restaurant.data.backgroundType,
    restaurant.data.backgroundValue,
    restaurant.data.backgroundImageUrl,
  ]);

  useEffect(() => {
    if (restaurant?.data?.timezone) {
      moment.tz.setDefault(restaurant?.data?.timezone);
    }
  }, [restaurant?.data?.timezone]);

  // Load custom fonts
  useEffect(() => {
    if (!isEmpty(restaurant.data.customFonts)) {
      restaurant.data.customFonts.forEach((font) => {
        const fontFace = new FontFace(font.fontName, `url('${font.fontSrc}')`);
        document.fonts.add(fontFace);
      });
    }
  }, [restaurant.data.customFonts]);

  // Set fonts from restaurant settings to body
  useEffect(() => {
    window.document.body.style.fontFamily = `${restaurant.data.generalFontFamilies}, 'Open Sans', 'Helvetica', 'sans-serif'`;
  }, [restaurant.data.generalFontFamilies, restaurant.data.customFonts]);

  // Set text colour from restaurant settings
  useEffect(() => {
    window.document.body.style.color = restaurant.data.mainTextColour;
  }, [restaurant.data.mainTextColour]);

  // Set favicon
  useEffect(() => {
    // set some defaults
    // window.document.body.style.fontFamily = `${typography.generalFontFamily}, 'Open Sans', 'Helvetica', 'sans-serif'`;
    setFavicon(isObee ? obeeIco : eatclubIco);
  }, []);

  /**
   * When a restaurant is loaded, try to initialize GTM
   */
  useEffect(() => {
    if (restaurant.success && !isEmpty(restaurant.data)) {
      if (restaurant.data.googleTagManager) {
        ReactGA.initialize(restaurant.data.googleTagManager, {
          name: 'venueGA',
          appVersion: packageInfo.version,
        });
      } else {
        // fallback to ec gtm
        ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID, {
          name: 'ECGA',
          appVersion: packageInfo.version,
        });
      }
      trackGooglePageview();
    }
  }, [restaurant.success]);

  /**
   * When a restaurant is loaded, try to get its facebook pixel
   */
  useEffect(() => {
    if (restaurant.success && !isEmpty(restaurant.data)) {
      if (isEmpty(integrations.data)) {
        fetchObeeIntegrations();
      }
    }
  }, [restaurant.success]);

  /**
   * Initialize facebook tracking once we have the data
   */
  useEffect(() => {
    if (!integrations?.facebookPixel) {
      // If facebook integration available, initialise the tracking
      const facebookIntegration = integrations?.data?.find(
        (integration) => integration?.id === 'Meta',
      );

      // Prioritise the integration pixel
      if (facebookIntegration?.active && facebookIntegration?.data?.pixel_id) {
        initializeFacebook(facebookIntegration?.data?.pixel_id);
      } else if (restaurant.data?.facebookMetaPixel) {
        initializeFacebook(restaurant.data?.facebookMetaPixel);
      }
    }
  }, [integrations?.data]);

  // theme is dependant on what the booking widget prem settings provide
  const appTheme = useMemo(
    () =>
      createTheme(
        restaurant.data.generalFontFamilies,
        restaurant.data.availabilityFontFamilies,
        restaurant.data.availabilityMainButtonBgColour,
        restaurant.data.availabilityMainButtonTextColour,
        restaurant.data.availabilityMainButtonCornerRadius,
        restaurant.data?.mainTextColour,
        restaurant.data?.outlineColour,
        restaurant.data?.activeOutlineColour,
        restaurant.data?.availabilityBackgroundBoxBgColour,
        restaurant.data.darkTheme,
        restaurant.data?.widgetHeaderBgColor,
        restaurant.data?.secondaryTextColour,
        restaurant.data?.secondaryTextOpacity,
        restaurant.data?.outlineOpacity,
        restaurant.data?.availabilityFieldsActiveStateColour,
        restaurant.data?.availabilityFieldsColourOpacity,
        restaurant.data?.availabilityFieldsOutlineColour,
        restaurant.data?.availabilityFieldsActiveOutlineColour,
        restaurant.data?.formHeaderBackgroundColor,
        restaurant.data?.formHeaderUnderlineColor,
        restaurant.data?.experienceActiveBackgroundColor,
        restaurant.data?.experienceActiveTextColor,
        restaurant.data?.dietaryTagBackgroundColor,
        restaurant.data?.dietaryTagTextColor,
        restaurant.data?.dietaryTagBorderColor,
        restaurant.data?.dietaryTagActiveBackgroundColor,
        restaurant.data?.dietaryTagActiveTextColor,
        restaurant.data?.dietaryTagActiveBorderColor,
        restaurant.data,
      ),
    [
      restaurant.data.generalFontFamilies,
      restaurant.data.availabilityFontFamilies,
      restaurant.data.availabilityMainButtonBgColour,
      restaurant.data.availabilityMainButtonTextColour,
      restaurant.data.availabilityMainButtonCornerRadius,
      restaurant.data?.mainTextColour,
      restaurant.data?.outlineColour,
      restaurant.data?.activeOutlineColour,
      restaurant.data?.availabilityBackgroundBoxBgColour,
      restaurant.data?.darkTheme,
      restaurant.data?.widgetHeaderBgColor,
      restaurant.data?.secondaryTextColour,
      restaurant.data?.secondaryTextOpacity,
      restaurant.data?.outlineOpacity,
      restaurant.data?.availabilityFieldsActiveStateColour,
      restaurant.data?.availabilityFieldsColourOpacity,
      restaurant.data?.availabilityFieldsOutlineColour,
      restaurant.data?.availabilityFieldsActiveOutlineColour,
      restaurant.data?.formHeaderBackgroundColor,
      restaurant.data?.formHeaderUnderlineColor,
      restaurant.data?.experienceActiveBackgroundColor,
      restaurant.data?.experienceActiveTextColor,
      restaurant.data?.dietaryTagBackgroundColor,
      restaurant.data?.dietaryTagTextColor,
      restaurant.data?.dietaryTagBorderColor,
      restaurant.data?.dietaryTagActiveBackgroundColor,
      restaurant.data?.dietaryTagActiveTextColor,
      restaurant.data?.dietaryTagActiveBorderColor,
      restaurant.data,
    ],
  );

  return (
    <ThemeProvider theme={appTheme}>
      <ErrorBoundary fallbackUI={({ error }) => <ErrorDisplay error={error} />}>
        <LegacyRedirect>
          <SwitchRoutes />
        </LegacyRedirect>
        <Toast />
      </ErrorBoundary>
    </ThemeProvider>
  );
};

App.propTypes = {
  integrations: integrationsPropTypes.isRequired,
  fetchObeeIntegrations: PropTypes.func.isRequired,
  initializeFacebook: PropTypes.func.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  setPreviewWidgetSettings: PropTypes.func.isRequired,
  applyPreviewStep: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  integrations: state.integrations,
  restaurant: state.restaurant,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      initializeFacebook: initializeFacebookAction,
      fetchObeeIntegrations: fetchObeeIntegrationsAction,
      setPreviewWidgetSettings: setPreviewWidgetSettingsAction,
      applyPreviewStep: applyPreviewStepAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(App);
